import { ComponentProps, forwardRef, useImperativeHandle } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { useCategoryOptions } from "@utils/hooks/useCategoryOptions";

import { Button } from "@design-system/Button/Button";
import { FieldGroup } from "@design-system/FieldGroup/FieldGroup";
import { Form } from "@design-system/Form/Form";
import { Select } from "@design-system/Select/Select";
import { TextField } from "@design-system/TextField/TextField";

export type AdminApplicationFilterFormValues = {
  search?: string | undefined;
  isSaasFilter?: "all" | "saas" | "non-saas" | undefined;
  categoryId?: string | undefined;
};

export type AdminApplicationFilterFormRef = {
  getCurrentValues: () => AdminApplicationFilterFormValues;
};

type Props = {
  submitText?: string;
  onFilterChange: SubmitHandler<AdminApplicationFilterFormValues>;
  values: AdminApplicationFilterFormValues;
  errorsAPI?: ComponentProps<typeof Form>["errorsAPI"];
  disabled?: boolean;
  withSubmitButton?: boolean;
};

const isSaasFilterOptions = [
  { id: "all", label: "All Resources" },
  { id: "saas", label: "SaaS Only" },
  { id: "non-saas", label: "Non-SaaS Only" },
];

export const AdminApplicationFilterForm = forwardRef<
  AdminApplicationFilterFormRef,
  Props
>(({ onFilterChange, values, errorsAPI }, ref) => {
  const { handleSubmit, setValue, watch, getValues } =
    useForm<AdminApplicationFilterFormValues>({
      values: {
        search: values.search ?? undefined,
        isSaasFilter: values.isSaasFilter ?? "all",
        categoryId: values.categoryId ?? undefined,
      },
    });

  const search = watch("search");
  const isSaasFilter = watch("isSaasFilter");
  const categoryId = watch("categoryId");

  const { categoryOptions } = useCategoryOptions();

  useImperativeHandle(
    ref,
    () => ({
      getCurrentValues: () => getValues(),
    }),
    [getValues],
  );

  return (
    <Form onSubmit={handleSubmit(onFilterChange)} errorsAPI={errorsAPI}>
      <FieldGroup label="Search">
        <TextField
          placeholder="Search by name, vendor, domain, or description..."
          value={search ?? ""}
          onChange={(e) =>
            setValue("search", e.target.value || undefined, {
              shouldDirty: true,
            })
          }
        />
      </FieldGroup>

      <FieldGroup label="Resource Type">
        <Select
          value={isSaasFilter ?? "all"}
          onChange={(value) =>
            setValue("isSaasFilter", value as AdminApplicationFilterFormValues["isSaasFilter"], {
              shouldDirty: true,
            })
          }
          options={isSaasFilterOptions}
        />
      </FieldGroup>

      <FieldGroup label="Category">
        <Select
          value={categoryId ?? ""}
          onChange={(value) =>
            setValue("categoryId", typeof value === 'string' ? value || undefined : undefined, {
              shouldDirty: true,
            })
          }
          options={categoryOptions}
        />
      </FieldGroup>

      <Button type="submit" style={{ display: "none" }} />
    </Form>
  );
});

AdminApplicationFilterForm.displayName = "AdminApplicationFilterForm";
